import { useMemo, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getNavigationItemsForRole,
  isPathActive,
  getNavigationItemByPath,
  shouldUpdateNavigation,
  getActiveNavigationItems,
  debugPathMatch,
} from "../config/navigation";
import { canAccessPath } from "../config/routes";

/**
 * Custom hook for navigation state management
 * Provides navigation items, active state detection, and related utilities
 * @returns {Object} Navigation state and utilities
 */
export const useNavigation = () => {
  const { user } = useAuth();
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);

  // Get navigation items for the current user's role, then gate each item by
  // the JWT modules: the Welcome Survey item requires the welcome_survey
  // module, every other item requires the general_suite module. This applies
  // to all user types.
  const navigationItems = useMemo(() => {
    if (!user) return [];
    const items = getNavigationItemsForRole(user.role, user.permissions || []);
    return items.filter(
      item =>
        canAccessPath(user.modules, item.path) &&
        (!item.requiresLeadershipTeam || user.leadershipTeam)
    );
  }, [user]);

  // Track route changes for performance optimization and debugging
  useEffect(() => {
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;

    // Only update if navigation state should change
    if (shouldUpdateNavigation(previousPath, currentPath)) {
      // Perform any side effects when navigation changes
      // This could include analytics, logging, etc.
      if (process.env.NODE_ENV === "development") {
        // Navigation updated
      }
    }

    previousPathRef.current = currentPath;
  }, [location.pathname, navigationItems]);

  // Get current active navigation item
  const activeNavigationItem = useMemo(() => {
    return getNavigationItemByPath(location.pathname);
  }, [location.pathname]);

  // Check if a specific navigation item is active (memoized for performance)
  const isNavigationItemActive = useMemo(() => {
    return navigationItem => {
      if (!navigationItem) {
        if (process.env.NODE_ENV === "development") {
          // isNavigationItemActive called with null/undefined navigationItem
        }
        return false;
      }

      const isActive = isPathActive(navigationItem.path, location.pathname);

      // Debug logging for development
      if (process.env.NODE_ENV === "development" && isActive) {
        // Navigation item active for path
      }

      return isActive;
    };
  }, [location.pathname]);

  // Get active navigation item ID
  const activeNavigationId = useMemo(() => {
    const activeItem = navigationItems.find(item =>
      isPathActive(item.path, location.pathname)
    );
    return activeItem ? activeItem.id : null;
  }, [navigationItems, location.pathname]);

  // Check if user has access to a specific path
  const hasAccessToPath = useMemo(() => {
    return path => {
      if (!user) return false;
      const item = getNavigationItemByPath(path);
      return item ? item.roles.includes(user.role) : false;
    };
  }, [user]);

  // Debug utilities (only available in development)
  const debugUtils = useMemo(() => {
    if (process.env.NODE_ENV !== "development") {
      return {};
    }

    return {
      getActiveItems: () => getActiveNavigationItems(location.pathname),
      debugPath: itemPath => debugPathMatch(itemPath, location.pathname),
      logActiveState: () => {
        // Navigation Debug Info
      },
    };
  }, [location.pathname]);

  // Force re-render when route changes to ensure active state updates
  const forceNavigationUpdate = useCallback(() => {
    // This function can be called to force a navigation state update
    // Useful for debugging or manual refresh scenarios
    if (process.env.NODE_ENV === "development") {
      // Forcing navigation update for path
    }
  }, []);

  return {
    navigationItems,
    activeNavigationItem,
    activeNavigationId,
    isNavigationItemActive,
    hasAccessToPath,
    currentPath: location.pathname,
    userRole: user?.role || null,
    previousPath: previousPathRef.current,
    forceNavigationUpdate,
    ...(process.env.NODE_ENV === "development" && { debug: debugUtils }),
  };
};

export default useNavigation;
